import {useCallback} from 'react';
import usePrepareCryptoPurchaseMutation from 'src/api/graphql/usePrepareCryptoPurchaseMutation';
import {getStore} from 'src/v2/state/store';
import {convertGraphQlToTransactionDetails} from 'src/v2/util/convertGraphQlToStoreState';
import type {TransactionDetails} from 'src/v2/state/types';
import {
  OpenapiCryptocurrencyPayinsResourceConsumerWalletSessionProviderEnum,
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum,
  OpenapiPostV1CryptoInternalPayinSessionPrepareInputNetworkEnum,
} from '@stripe-internal/data-v1/schema/types';

interface PreparePaymentParams {
  selectedCurrency: OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum;
  walletAddress: string;
  network: OpenapiPostV1CryptoInternalPayinSessionPrepareInputNetworkEnum;
  walletProvider: OpenapiCryptocurrencyPayinsResourceConsumerWalletSessionProviderEnum;
}

export const usePrepareSessionForPayment = () => {
  const store = getStore();
  const [prepareCryptoPurchase] = usePrepareCryptoPurchaseMutation();
  const sessionId = store.useSelector((state) => state.id);
  const clientSecret = store.useSelector((state) => state.clientSecret);

  return useCallback(
    async (
      params: PreparePaymentParams,
    ): Promise<{transactionDetails: TransactionDetails}> => {
      const response = await prepareCryptoPurchase({
        variables: {
          input: {
            client_secret: clientSecret,
            crypto_payin_session: sessionId,
            network: params.network,
            currency: params.selectedCurrency,
            wallet_provider: params.walletProvider,
            wallet_address: params.walletAddress,
          },
        },
      });

      const updatedSession = response.data?.v1CryptoInternalPayinSessionPrepare;

      if (response.error) {
        throw response.error;
      }

      if (!updatedSession) {
        throw new Error('No session returned from prepareCryptoPurchase');
      }

      const updatedTransactionDetails = convertGraphQlToTransactionDetails(
        updatedSession.transaction_details,
      );

      store.setValue((state) => {
        state.state = updatedSession.state;
        state.transactionDetails = updatedTransactionDetails;
        state.validBefore = updatedSession.valid_before;
      });

      return {
        transactionDetails: updatedTransactionDetails,
      };
    },
    [sessionId, clientSecret, prepareCryptoPurchase, store],
  );
};
